import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { auditLogsApi, usersApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import type { AuditLog, AuditEntityType, AuditAction, AuditLogFilters } from '@/types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_ENTITY_TYPES = '__all__'
const ALL_ACTIONS = '__all__'
const ALL_USERS = '__all__'

const ENTITY_TYPES: AuditEntityType[] = [
  'CLIENT', 'BUSINESS', 'INVOICE', 'QUOTE', 'EXPENSE', 'RECURRING_INVOICE',
  'USER', 'ROLE', 'TAX', 'FEES', 'CATEGORY', 'EMAIL_TEMPLATE', 'BILLING_TEMPLATE',
  'AGENCY_CONFIG', 'CUSTOM_FIELD', 'PROJECT', 'BOARD', 'CARD', 'LIST', 'COMMENT',
  'ATTACHMENT', 'SUPPORT_TICKET'
]

const ACTIONS: AuditAction[] = [
  'CREATE', 'UPDATE', 'DELETE', 'VIEW', 'EXPORT', 'SEND', 'LOGIN', 'LOGOUT',
  'APPROVE', 'REJECT', 'CONVERT', 'ARCHIVE', 'RESTORE', 'DOWNLOAD'
]

interface FilterState {
  searchTerm: string
  entityType: string
  action: string
  userId: string
  startDate: string
  endDate: string
}

export default function AuditLogsPage() {
  const { t } = useTranslation()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    entityType: ALL_ENTITY_TYPES,
    action: ALL_ACTIONS,
    userId: ALL_USERS,
    startDate: '',
    endDate: '',
  })

  // Build API filters
  const apiFilters = useMemo((): AuditLogFilters => {
    const f: AuditLogFilters = {
      page: currentPage,
      page_size: pageSize,
    }
    if (filters.searchTerm) f.search = filters.searchTerm
    if (filters.entityType !== ALL_ENTITY_TYPES) f.entity_type = filters.entityType as AuditEntityType
    if (filters.action !== ALL_ACTIONS) f.action = filters.action as AuditAction
    if (filters.userId !== ALL_USERS) f.user_id = filters.userId
    if (filters.startDate) f.start_date = filters.startDate
    if (filters.endDate) f.end_date = filters.endDate
    return f
  }, [currentPage, pageSize, filters])

  const { data: auditData, isLoading } = useQuery({
    queryKey: ['audit-logs', apiFilters],
    queryFn: () => auditLogsApi.getAll(apiFilters),
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  })

  const logs = auditData?.logs || []
  const totalRecords = auditData?.total_count || 0
  const totalPages = auditData?.total_pages || 1

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const hasActiveFilters = filters.searchTerm || filters.entityType !== ALL_ENTITY_TYPES ||
    filters.action !== ALL_ACTIONS || filters.userId !== ALL_USERS ||
    filters.startDate || filters.endDate

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      entityType: ALL_ENTITY_TYPES,
      action: ALL_ACTIONS,
      userId: ALL_USERS,
      startDate: '',
      endDate: '',
    })
    setCurrentPage(1)
  }

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const toggleRowExpanded = (logId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(logId)) {
        next.delete(logId)
      } else {
        next.add(logId)
      }
      return next
    })
  }

  const getActionBadgeVariant = (action: AuditAction): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (action) {
      case 'CREATE': return 'default'
      case 'UPDATE': return 'secondary'
      case 'DELETE': return 'destructive'
      default: return 'outline'
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('auditLogs.title')}</h1>
        <p className="text-muted-foreground">{t('auditLogs.description')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            placeholder={t('auditLogs.searchPlaceholder')}
            value={filters.searchTerm}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value })
              setCurrentPage(1)
            }}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.entityType}
          onValueChange={(value) => {
            setFilters({ ...filters, entityType: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('auditLogs.allEntityTypes')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ENTITY_TYPES}>{t('auditLogs.allEntityTypes')}</SelectItem>
            {ENTITY_TYPES.map((type) => (
              <SelectItem key={type} value={type}>{type.replace(/_/g, ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.action}
          onValueChange={(value) => {
            setFilters({ ...filters, action: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('auditLogs.allActions')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ACTIONS}>{t('auditLogs.allActions')}</SelectItem>
            {ACTIONS.map((action) => (
              <SelectItem key={action} value={action}>{action}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.userId}
          onValueChange={(value) => {
            setFilters({ ...filters, userId: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('auditLogs.allUsers')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_USERS}>{t('auditLogs.allUsers')}</SelectItem>
            {users.map((user) => (
              <SelectItem key={user.user_id} value={user.user_id}>{user.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="date"
          value={filters.startDate}
          onChange={(e) => {
            setFilters({ ...filters, startDate: e.target.value })
            setCurrentPage(1)
          }}
          className="w-[150px]"
          placeholder={t('auditLogs.startDate')}
        />

        <Input
          type="date"
          value={filters.endDate}
          onChange={(e) => {
            setFilters({ ...filters, endDate: e.target.value })
            setCurrentPage(1)
          }}
          className="w-[150px]"
          placeholder={t('auditLogs.endDate')}
        />

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      {/* Table */}
      <AuditLogsTable
        logs={logs}
        isLoading={isLoading}
        expandedRows={expandedRows}
        onToggleRow={toggleRowExpanded}
        getActionBadgeVariant={getActionBadgeVariant}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRecords={totalRecords}
        totalPages={totalPages}
        startRecord={startRecord}
        endRecord={endRecord}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
        onPreviousPage={() => setCurrentPage(p => Math.max(p - 1, 1))}
        t={t}
      />
    </div>
  )
}

interface AuditLogsTableProps {
  logs: AuditLog[]
  isLoading: boolean
  expandedRows: Set<string>
  onToggleRow: (logId: string) => void
  getActionBadgeVariant: (action: AuditAction) => 'default' | 'secondary' | 'destructive' | 'outline'
  pageSize: number
  currentPage: number
  totalRecords: number
  totalPages: number
  startRecord: number
  endRecord: number
  onPageSizeChange: (size: string) => void
  onNextPage: () => void
  onPreviousPage: () => void
  t: (key: string) => string
}

function AuditLogsTable({
  logs,
  isLoading,
  expandedRows,
  onToggleRow,
  getActionBadgeVariant,
  pageSize,
  currentPage,
  totalRecords,
  totalPages,
  startRecord,
  endRecord,
  onPageSizeChange,
  onNextPage,
  onPreviousPage,
  t,
}: AuditLogsTableProps) {
  if (isLoading) {
    return <div className="text-center py-8">{t('common.loading')}</div>
  }

  if (logs.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('auditLogs.noLogsFound')}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-280px)]">
      <Card className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead>{t('auditLogs.timestamp')}</TableHead>
                <TableHead>{t('auditLogs.user')}</TableHead>
                <TableHead>{t('auditLogs.entityType')}</TableHead>
                <TableHead>{t('auditLogs.entityName')}</TableHead>
                <TableHead>{t('auditLogs.action')}</TableHead>
                <TableHead>{t('auditLogs.descriptionCol')}</TableHead>
                <TableHead>{t('auditLogs.ipAddress')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <>
                  <TableRow key={log.log_id} className="cursor-pointer" onClick={() => onToggleRow(log.log_id)}>
                    <TableCell>
                      {(log.changes && log.changes.length > 0) || log.old_values || log.new_values ? (
                        expandedRows.has(log.log_id) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(log.created_at), 'MMM dd, yyyy HH:mm:ss')}
                    </TableCell>
                    <TableCell>{log.user_name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{log.entity_type.replace(/_/g, ' ')}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{log.entity_name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={getActionBadgeVariant(log.action)}>{log.action}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate">{log.description || '-'}</TableCell>
                    <TableCell>{log.ip_address || '-'}</TableCell>
                  </TableRow>
                  {expandedRows.has(log.log_id) && (
                    <TableRow key={`${log.log_id}-details`}>
                      <TableCell colSpan={8} className="bg-muted/50 p-4">
                        <ChangeDetails log={log} t={t} />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination Controls */}
        <div className="flex items-center gap-3 px-4 py-2 border-t">
          <Select value={String(pageSize)} onValueChange={onPageSizeChange}>
            <SelectTrigger className="w-[70px] h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {startRecord}-{endRecord} {t('common.of')} {totalRecords}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onPreviousPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onNextPage}
              disabled={currentPage >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

function ChangeDetails({ log, t }: { log: AuditLog; t: (key: string) => string }) {
  if (log.changes && log.changes.length > 0) {
    return (
      <div className="space-y-2">
        <h4 className="font-medium text-sm">{t('auditLogs.changesTitle')}</h4>
        <div className="grid gap-2">
          {log.changes.map((change, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-4 text-sm">
              <div className="font-medium">{change.field}</div>
              <div className="text-muted-foreground">
                <span className="text-red-600">{JSON.stringify(change.old_value)}</span>
              </div>
              <div>
                <span className="text-green-600">{JSON.stringify(change.new_value)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (log.old_values || log.new_values) {
    return (
      <div className="grid grid-cols-2 gap-4">
        {log.old_values && (
          <div>
            <h4 className="font-medium text-sm mb-2">{t('auditLogs.oldValues')}</h4>
            <pre className="text-xs bg-background p-2 rounded overflow-auto max-h-[200px]">
              {JSON.stringify(log.old_values, null, 2)}
            </pre>
          </div>
        )}
        {log.new_values && (
          <div>
            <h4 className="font-medium text-sm mb-2">{t('auditLogs.newValues')}</h4>
            <pre className="text-xs bg-background p-2 rounded overflow-auto max-h-[200px]">
              {JSON.stringify(log.new_values, null, 2)}
            </pre>
          </div>
        )}
      </div>
    )
  }

  return <div className="text-sm text-muted-foreground">{t('auditLogs.noDetails')}</div>
}

